import React from 'react';
import './UnrealizedCostBasisTableV2.scss';
import { Dollar } from 'components/shared/Text/Text';
import ValueIndicator from 'components/shared/ValueIndicator/ValueIndicator';
import CarrotToggle from 'components/shared/Table/CarrotToggle';
import {
  ACTIVE_ADVISORY,
  ACTIVE_PLUS,
  ADVISORY,
  BROKERAGE,
  CATEGORY,
  MUTUAL_FUND,
  RETAIL,
  SMALL_BUSINESS_RETIREMENT
} from 'services/Products';
import { Link } from 'react-router-dom';
import numeral from 'numeral';
import {
  realizedTableColumns,
  unrealizedTableColumns,
  unrealizedTableHeaders
} from 'components/shared/Text/ColumnHeaders';
import { activePlusAccountUrl, planningDetailsUrl } from 'services/Externals';
import CostBasisErrorV2 from './CostBasisErrorV2';
import { getProductName } from 'services/Products';

// class name variables
const percentGainLossClass = 'percent-gain-loss-val';
const uncoveredCoveredClass = 'covered-uncovered-cell-v2 b-font-weight-bold b-typography-sans-serif--caption--300';
const accountNameLinkClass = 'account-name-link b-font-weight-bold b-typography-sans-serif--headline--50';
const toggleIcon = 'toggle-icon';
const expandedHeader = 'expanded-header';
const accountInfoContainer = 'account-info-container';

// filter out to only include category types of Retail, Advisory, or Small Business Retirement
// for Small Business Retirement, include only account groups that are Retail or Advisory
export function filterHierarchy(taxonomyHierarchy, taxonomyGroups) {
  return taxonomyHierarchy.filter((hierarchy) => {
    const categoryGroup = taxonomyGroups.find(
      (group) => group.tier === CATEGORY && group.id === hierarchy.id
    );
    return categoryGroup.type === RETAIL || categoryGroup.type === ADVISORY || categoryGroup.type === SMALL_BUSINESS_RETIREMENT;
  });
}

// group the agreements by agreementId field -
// due to the rows being dynamic when toggles are expanded and are added to the tableRows array
// Ex. match the holdings, covered, and noncovered to their parent account
export const groupAgreementsWithChildrenRows = (agreements) => {
  const grouped = {};

  agreements.forEach((agreement) => {
    const agreementId = agreement.agreementID;
    if (!grouped[agreementId]) {
      grouped[agreementId] = [];
    }
    grouped[agreementId].push(agreement);
  });
  return Object.values(grouped);
};

// sort the grouped agreements by the parent accountName alphabetically
export const sortAgreementsByName = (groupedAgreements) => {
  groupedAgreements.sort((a, b) => {
    const accountNameA = a[0].accountName;
    const accountNameB = b[0].accountName;
    if (accountNameA < accountNameB) {
      return -1;
    }
    if (accountNameA > accountNameB) {
      return 1;
    }
    return 0;
  });

  return groupedAgreements.flat();
};

// missing cost basis data flag - only for mutual fund account - if non-covered share quantity>0 and cost basis amount=0
export function checkIfMissingCostBasisData(uncoveredRow) {
  const { costBasisAmount, shares } = uncoveredRow;
  return shares > 0 && (costBasisAmount === 0 || costBasisAmount === '-');
}

// checks if missing all uncovered data - all values equal to zero
export function areAllFieldsZero(row, fields) {
  return fields.every((field) => {
    return row[field] === 0 || row[field] === '-';
  });
}

// mapped mutual fund data for cost basis table
export function mfUnrealizedCostBasisData(costBasisBalances) {
  const flattenedCostBasisData = [];
  costBasisBalances.forEach((account) => {
    account.rows.forEach((row) => {
      if (row.balance.positions && row.balance.positions.length > 0) {
        row.balance.positions.forEach((position) => {
          flattenedCostBasisData.push({
            accountType: account.accountType,
            taxesUniqueId: account.accountId,
            ...row.account,
            accountGainLossPercent: row.balance.total.gainLossPercent,
            accountCostPerShare: row.balance.total.costPerShare,
            accountTotals: row.balance.total,
            covered: {
              positionGainLossPercent: position.total.gainLossPercent,
              positionCostPerShare: position.total.costPerShare,
              investmentVehicle: position.investmentVehicle,
              costBasisMethod: position.costBasisMethod,
              covered: 'Covered',
              ...position.covered
            },
            uncovered: {
              positionGainLossPercent: position.total.gainLossPercent,
              positionCostPerShare: position.total.costPerShare,
              investmentVehicle: position.investmentVehicle,
              costBasisMethod: position.costBasisMethod,
              covered: 'Noncovered',
              ...position.noncovered
            }
          });
        });
      } else {
        flattenedCostBasisData.push({
          accountType: account.accountType,
          taxesUniqueId: account.accountId,
          ...row.account,
          accountGainLossPercent: row.balance.total.gainLossPercent,
          accountCostPerShare: row.balance.total.costPerShare,
          accountCostBasisAmount: row.balance.total.costBasisAmount,
          accountGainLossAmount: row.balance.total.gainLossAmount,
          accountMarketValue: row.balance.total.marketValue,
          accountShares: row.balance.total.shares,
          covered: {
            covered: 'Covered',
            ...row.balance.covered
          },
          uncovered: {
            covered: 'Noncovered',
            ...row.balance.noncovered
          }
        });
      }
    });
  });
  return flattenedCostBasisData;
}

export function costBasisRealizedData(costBasisBalances) {
  const flattenedCostBasisData = [];
  costBasisBalances.forEach((account) => {
    account.rows.forEach((row) => {
      if (row.balance.positions && row.balance.positions.length > 0) {
        row.balance.positions.forEach((position) => {
          flattenedCostBasisData.push({
            accountType: account.accountType,
            ...row.account,
            costBasisAmount: row.balance.total.costBasisAmount,
            longTermGainLossAmount: row.balance.total.longTermGainLossAmount,
            longTermGainLossPercent: row.balance.total.longTermGainLossPercent,
            proceeds: row.balance.total.proceeds,
            shortTermGainLossAmount: row.balance.total.shortTermGainLossAmount,
            shortTermGainLossPercent:
            row.balance.total.shortTermGainLossPercent,
            totalGainLossAmount: row.balance.total.totalGainLossAmount,
            totalGainLossPercent: row.balance.total.totalGainLossPercent,
            covered: {
              investmentVehicle: position.investmentVehicle,
              costBasisMethod: position.costBasisMethod,
              covered: 'Covered',
              ...position.covered
            },
            uncovered: {
              investmentVehicle: position.investmentVehicle,
              costBasisMethod: position.costBasisMethod,
              covered: 'Noncovered',
              ...position.noncovered
            }
          });
        });
      } else {
        flattenedCostBasisData.push({
          accountType: account.accountType,
          ...row.account,
          costBasisAmount: row.balance.total.costBasisAmount,
          longTermGainLossAmount: row.balance.total.longTermGainLossAmount,
          longTermGainLossPercent: row.balance.total.longTermGainLossPercent,
          proceeds: row.balance.total.proceeds,
          shortTermGainLossAmount: row.balance.total.shortTermGainLossAmount,
          shortTermGainLossPercent: row.balance.total.shortTermGainLossPercent,
          totalGainLossAmount: row.balance.total.totalGainLossAmount,
          totalGainLossPercent: row.balance.total.totalGainLossPercent,
          covered: {
            covered: 'Covered',
            ...row.balance.covered
          },
          uncovered: {
            covered: 'Noncovered',
            ...row.balance.noncovered
          }
        });
      }
    });
  });
  return flattenedCostBasisData;
}

// generates the tier 2 and 3 rows (navy color rows) - category and account type rows
export const generateCategoryAndAccountTypeRows = (
  className,
  name,
  columnHeaders,
  missingCovered = false,
  agreementID = ''
) => {
  const rows = columnHeaders.slice(0, -1).map((column) => ({
    record: (
      <div className={missingCovered ? 'cell-v2' : `dollar-cell-v2 ${column}`}>
        {missingCovered && '-'}
      </div>
    ),
    variant: 'text'
  }));

  return {
    agreementID,
    className,
    rowArray: [
      {
        record: (
          <p className={missingCovered && uncoveredCoveredClass}>{name}</p>
        ),
        variant: 'text'
      },
      ...rows
    ]
  };
};

export const AccountNameLink = ({
                                  productType,
                                  isRetirementAdvisoryServiceClient,
                                  isHolding,
                                  agreement,
                                  rowData,
                                  isBrokerage,
                                  isCWV = false,
                                  uberId = false,
                                }) => {
  const { accountName, taxesUniqueId } = rowData;
  const { agreementName, isViewOnly } = agreement;
  if (productType === MUTUAL_FUND) {
    return (
      <Link
        className={accountNameLinkClass}
        to={`/account-details?id=${taxesUniqueId}`}>
        {accountName}
      </Link>
    );
  } else if (productType === ACTIVE_PLUS && !isHolding) {
    return (
      <a
        className={accountNameLinkClass}
        href={activePlusAccountUrl(taxesUniqueId, isCWV, uberId, false)}>
        {agreementName}
      </a>
    );
  } else if (
    productType === ACTIVE_ADVISORY &&
    isRetirementAdvisoryServiceClient &&
    !isHolding
  ) {
    return (
      <a className={accountNameLinkClass} href={planningDetailsUrl(isCWV)}>
        {agreementName}
      </a>
    );
  } else if (isBrokerage && !isViewOnly) {
    const  {agreementKey: { agreementID }} = agreement;
    const { cusip } = rowData;
    return (
      <Link
        className={accountNameLinkClass}
        to={`/account-details?id=${agreementID}&investment=${cusip}`}>
        {isHolding ? accountName : agreementName}
      </Link>
    )
  } else {
    return (
      <div className={isHolding && (productType !== BROKERAGE) ? 'holding-info b-font-weight-bold b-typography-sans-serif--caption--300' : 'b-font-weight-bold b-typography-sans-serif--headline--50'}>
        {isHolding ? accountName : agreementName}
      </div>
    );
  }
};

// creates account info section
export const AccountInfo = ({
                              rowData,
                              agreement,
                              isRealized,
                              isHolding = false,
                              isRetirementAdvisoryServiceClient = false,
                              isBrokerage = false,
                              isRASOrAPP = false,
                              isCWV,
                              uberId,
                            }) => {
  const { agreementNumberDisplay, agreementKey } = agreement;
  const { productType } = agreementKey;
  const { ticker } = rowData;

  return (
    <div className="account-info">
      <div className="account-info-ticker b-color-text--set--navy b-font-weight-bold b-typography-sans-serif--caption--200">{ticker}</div>
      <div className="account-info-name b-color-text--set--navy b-font-weight-bold">
        {isRealized ? (
          <div className="b-font-weight-bold b-typography-sans-serif--headline--50">
            {
              isHolding ? (
                rowData.accountName
              ) : (
                agreement.agreementName
              )
            }
          </div>
        ) : (
          <AccountNameLink
            productType={productType.enumerationID}
            isRetirementAdvisoryServiceClient={
              isRetirementAdvisoryServiceClient
            }
            isHolding={isHolding}
            agreement={agreement}
            rowData={rowData}
            isBrokerage={isBrokerage}
            isCWV={isCWV}
            uberId={uberId}
          />
        )}
      </div>
      <div className="account-info-check-sum b-color-text--set--navy b-font-weight-light b-typography-sans-serif--caption--200 blocked_data">
        {((!isRASOrAPP && isHolding) || !isHolding) && agreementNumberDisplay}
      </div>
    </div>
  );
};

// creates the covered/uncovered rows for unrealized accounts/holdings
export const getUnrealizedCoveredRows = (
  agreementID,
  row,
  shouldShowLastRow,
  missingCostBasisData,
  hasBrokerageRASOrAdvisoryFund,
  shortTermGainLossAmount = '-',
  longTermGainLossAmount = '-',
  isBrokerageOrRasOrAPP = false
) => {
  const {
    costBasisAmount,
    costPerShare,
    covered,
    gainLossAmount,
    gainLossPercent,
    marketValue,
    shares
  } = row;
  const rowArray = [
    {
      record: <div className={uncoveredCoveredClass}>{covered}</div>,
      variant: 'text',
      id: 'cds-account-cell-v2'
    },
    {
      record: (
        <div className={'cell-v2'}>{numeral(shares).format('0,0.0000')}</div>
      ),
      variant: 'text',
      id: 'cds-shares-cell-v2'
    },
    {
      record: (
        <div className={'cell-v2'}>
          {missingCostBasisData ? '-' : <Dollar>{costPerShare}</Dollar>}
        </div>
      ),
      variant: 'text',
      id: 'cds-share-cost-cell-v2'
    },
    {
      record: (
        <div className={'cell-v2'}>
          {missingCostBasisData ? '-' : <Dollar>{costBasisAmount}</Dollar>}
        </div>
      ),
      variant: 'text',
      id: 'cds-amount-cell-v2'
    },
    {
      record: (
        <div className={'cell-v2'}>
          <Dollar>{marketValue}</Dollar>
        </div>
      ),
      variant: 'text',
      id: 'cds-market-value-cell-v2'
    }
  ];

  if (hasBrokerageRASOrAdvisoryFund) {
    rowArray.push(
      {
        record: (
          <div className={'cell-v2'}>
            {missingCostBasisData || shortTermGainLossAmount === '-' ? (
              '-'
            ) : (
              <>
                <div className="value-indicator-icon">
                  <ValueIndicator value={shortTermGainLossAmount} />
                </div>
                <Dollar>{shortTermGainLossAmount}</Dollar>
              </>
            )}
          </div>
        ),
        variant: 'text',
        id: 'cds-short-term-cell-v2'
      },
      {
        record: (
          <div className={'cell-v2'}>
            {missingCostBasisData || longTermGainLossAmount === '-' ? (
              '-'
            ) : (
              <>
                <div className="value-indicator-icon">
                  <ValueIndicator value={longTermGainLossAmount} />
                </div>
                <Dollar>{longTermGainLossAmount}</Dollar>
              </>
            )}
          </div>
        ),
        variant: 'text',
        id: 'cds-long-term-cell-v2'
      }
    );
  }

  rowArray.push({
    record: (
      <div className={'cell-v2'}>
        {missingCostBasisData ? (
          '-'
        ) : (
          <>
            <div className="value-indicator-icon">
              <ValueIndicator value={gainLossAmount} />
            </div>
            <Dollar>{gainLossAmount}</Dollar>
            <br />
            <div className={percentGainLossClass}>
              {numeral(isBrokerageOrRasOrAPP ? gainLossPercent : (gainLossPercent * 100)).format('0,0.00')}%
            </div>
          </>
        )}
      </div>
    ),
    variant: 'text',
    id: 'cds-gain-loss-cell-v2'
  });

  return {
    agreementID,
    rowArray: rowArray
  };
};

export const getRealizedCoveredRows = (
  index,
  row,
  shouldShowLastRow,
  noCoveredData,
  missingUncovered,
  isBrokerageOrRasOrAPP = false
) => {
  const {
    costBasisAmount,
    covered,
    longTermGainLossAmount,
    proceeds,
    shortTermGainLossAmount,
    totalGainLossAmount,
    totalGainLossPercent
  } = row;
  return {
    agreementID: index,
    rowArray: [
      {
        record: <div className={uncoveredCoveredClass}>{covered}</div>,
        variant: 'text',
        id: 'cds-account-cell-v2'
      },
      {
        record: (
          <div className={'cell-v2'}>
            {noCoveredData || missingUncovered ? (
              '-'
            ) : (
              <>
                <div className="value-indicator-icon">
                  <ValueIndicator value={shortTermGainLossAmount} />
                </div>
                <Dollar>{shortTermGainLossAmount}</Dollar>
              </>
            )}
          </div>
        ),
        variant: 'text',
        id: 'cds-short-term-cell-v2'
      },
      {
        record: (
          <div className={'cell-v2'}>
            {noCoveredData || missingUncovered ? (
              '-'
            ) : (
              <>
                <div className="value-indicator-icon">
                  <ValueIndicator value={longTermGainLossAmount} />
                </div>
                <Dollar>{longTermGainLossAmount}</Dollar>
              </>
            )}
          </div>
        ),
        variant: 'text',
        id: 'cds-long-term-cell-v2'
      },
      {
        record: (
          <div className={'cell-v2'}>
            {noCoveredData || missingUncovered ? (
              '-'
            ) : (
              <>
                <div className="value-indicator-icon">
                  <ValueIndicator value={totalGainLossAmount} />
                </div>
                <Dollar>{totalGainLossAmount}</Dollar>
                <br />
                <div className={percentGainLossClass}>
                  {numeral(isBrokerageOrRasOrAPP ? totalGainLossPercent : (totalGainLossPercent * 100)).format('0,0.00')}%
                </div>
              </>
            )}
          </div>
        ),
        variant: 'text',
        id: 'cds-gain-loss-cell-v2'
      },
      {
        record: (
          <div className={'cell-v2'}>
            {noCoveredData ? '-' : <Dollar>{proceeds}</Dollar>}
          </div>
        ),
        variant: 'text',
        id: 'cds-proceeds-cell-v2'
      },
      {
        record: (
          <div className={'cell-v2'}>
            {noCoveredData || missingUncovered ? (
              '-'
            ) : (
              <Dollar>{costBasisAmount}</Dollar>
            )}
          </div>
        ),
        variant: 'text',
        id: 'cds-cost-basis-cell-v2'
      }
    ]
  };
};

export const ExpandToggle = ({
                               index,
                               openedRows,
                               setOpenedRows,
                               className = ''
                             }) => {
  const handleClick = () => {
    setOpenedRows((prevState) => {
      return { ...prevState, [index]: !prevState[index] };
    });
  };

  return (
    <CarrotToggle
      isOpen={openedRows[index]}
      toggleChildrenView={handleClick}
      btnClass={className}
    />
  );
};

export const getUnrealizedAccountAndHoldingRow = (
  index,
  isMFAccount,
  openedRows,
  setOpenedRows,
  className,
  row,
  missingCostBasisData,
  agreement,
  isRetirementAdvisoryServiceClient,
  hasBrokerageRASOrAdvisoryFund,
  isBrokerage,
  parentID,
  noUncoveredData,
  noShortData,
  isHolding = false,
  shortTermGainLossAmount = '-',
  longTermGainLossAmount = '-',
  hasURZOrRZFail = false,
  isCWV,
  uberId,
) => {
  const {
    accountGainLossPercent,
    accountCostPerShare,
    accountCostBasisAmount,
    accountGainLossAmount,
    accountMarketValue,
    accountShares,
    recordStatus
  } = row;
  const decimalFormat = isMFAccount ? '0,0.000' : '0,0.0000';
  const { agreementKey  } = agreement;
  const productType = agreementKey?.productType?.enumerationID;
  const productName = getProductName(productType);
  const isRASOrAPP = productType === ACTIVE_PLUS || productType === ACTIVE_ADVISORY;
  const classNm = 'category-row-error';

  const {
    covered,
    uncovered,
    short
  } = row;
  const noUncoveredHoldingData = uncovered
    ? areAllFieldsZero(uncovered, unrealizedTableColumns)
    : true;
  const noShortHoldingData = short
    ? areAllFieldsZero(short, unrealizedTableColumns)
    : true;

  let rowArray = [];
  if ((recordStatus === 'UNKNOWN' || hasURZOrRZFail) && !isMFAccount) {
    rowArray = [
      {
        record: <CostBasisErrorV2 name={productName} />,
        variant: 'text',
        className: classNm,
        colSpan: '8'
      }
    ];
    return {
      className: classNm,
      rowArray: rowArray
    };
  } else {
    rowArray = [
      {
        record: (
          <div className="flex-container-v2">
            <div
              className={
                (!noUncoveredHoldingData || !noShortHoldingData || (isRASOrAPP && !isHolding))
                  ? 'account-info-container-with-toggle'
                  : isHolding && !isBrokerage
                    ? 'holding-info-container'
                    : accountInfoContainer
              }>
              {(!noUncoveredHoldingData || !noShortHoldingData || (isRASOrAPP && !isHolding)) && (
                <ExpandToggle
                  index={index}
                  openedRows={openedRows}
                  setOpenedRows={setOpenedRows}
                  className={className}
                />
              )}
              <AccountInfo
                rowData={row}
                agreement={agreement}
                isRealized={false}
                isHolding={isHolding}
                isRetirementAdvisoryServiceClient={
                  isRetirementAdvisoryServiceClient
                }
                isBrokerage={isBrokerage}
                isRASOrAPP={isRASOrAPP}
                isCWV={isCWV}
                uberId={uberId}
              />
            </div>
          </div>
        ),
        variant: 'text',
        className: className
      },
      {
        record: (
          <div className={'cell-v2'}>
            {/* Do not show shares or cost per share at account level for both RAS and APP */}
            {!isRASOrAPP || isHolding
              ? numeral(accountShares).format(decimalFormat)
              : ''}
          </div>
        ),
        variant: 'text',
        className: className
      },
      {
        record: (
          <div className={'cell-v2'}>
            {isRASOrAPP && !isHolding ? (
              ''
            ) : missingCostBasisData ? (
              '-'
            ) : (
              <Dollar format="$0,0.00">{accountCostPerShare}</Dollar>
            )}
          </div>
        ),
        variant: 'text',
        className: className
      },
      {
        record: (
          <div className={'cell-v2'}>
            {missingCostBasisData ? (
              '-'
            ) : (
              <Dollar>{accountCostBasisAmount}</Dollar>
            )}
          </div>
        ),
        variant: 'text',
        className: className
      },
      {
        record: (
          <div className={'cell-v2'}>
            <Dollar>{accountMarketValue}</Dollar>
          </div>
        ),
        variant: 'text',
        className: className
      }
    ];

    if (hasBrokerageRASOrAdvisoryFund) {
      rowArray.push(
        {
          record: (
            <div className={'cell-v2'}>
              {missingCostBasisData ||
              shortTermGainLossAmount === '-' ? (
                '-'
              ) : (
                <>
                  <div className="value-indicator-icon">
                    <ValueIndicator value={shortTermGainLossAmount} />
                  </div>
                  <Dollar>{shortTermGainLossAmount}</Dollar>
                </>
              )}
            </div>
          ),
          variant: 'text',
          className: className
        },
        {
          record: (
            <div className={'cell-v2'}>
              {missingCostBasisData ||
              longTermGainLossAmount === '-' ? (
                '-'
              ) : (
                <>
                  <div className="value-indicator-icon">
                    <ValueIndicator value={longTermGainLossAmount} />
                  </div>
                  <Dollar>{longTermGainLossAmount}</Dollar>
                </>
              )}
            </div>
          ),
          variant: 'text',
          className: className
        }
      );
    }

    rowArray.push({
      record: (
        <div className={'cell-v2'}>
          {missingCostBasisData ? (
            '-'
          ) : (
            <>
              <div
                className="value-indicator-icon"
                style={{ display: 'inline-block' }}>
                <ValueIndicator value={accountGainLossAmount} />
              </div>
              <Dollar>{accountGainLossAmount}</Dollar>
              <br />
              <div className={percentGainLossClass}>
                {numeral(isBrokerage || isRASOrAPP ? accountGainLossPercent : (accountGainLossPercent * 100)).format('0,0.00')}%
              </div>
            </>
          )}
        </div>
      ),
      variant: 'text',
      className: className
    });
    let childRow = [];

    if ((openedRows?.[index] && !isMFAccount && row.holdings)) {
      row.holdings.map((holding) => {
        const {taxesUniqueId, longTermGainLossAmount, missingCostBasis, shortTermGainLossAmount, uncovered, short} = holding;
        const noHoldingUncoveredData = uncovered
          ? areAllFieldsZero(uncovered, realizedTableColumns)
          : true;
        const noHoldingShortData = short
          ? areAllFieldsZero(short, realizedTableColumns)
          : true;
        const brkHoldingClassName = openedRows[taxesUniqueId]
          ? expandedHeader
          : toggleIcon;

        const holdingRow = getUnrealizedAccountAndHoldingRow(
          taxesUniqueId,
          isMFAccount,
          openedRows,
          setOpenedRows,
          isBrokerage ? brkHoldingClassName : 'holding-row',
          holding,
          missingCostBasis,
          agreement,
          isRetirementAdvisoryServiceClient,
          hasBrokerageRASOrAdvisoryFund,
          isBrokerage,
          parentID,
          noHoldingUncoveredData,
          noHoldingShortData,
          true,
          shortTermGainLossAmount,
          longTermGainLossAmount,
          hasURZOrRZFail,
          isCWV,
          uberId
        );
        childRow.push(holdingRow);
      });
    }
    if (openedRows?.[index] && (!noUncoveredHoldingData || !noShortHoldingData)) {
      if (covered) {
        childRow.push(
          getUnrealizedCoveredRows(
            isBrokerage ? row.taxesUniqueId : index,
            covered,
            false,
            covered.missingCostBasis,
            hasBrokerageRASOrAdvisoryFund,
            covered.shortTermGainLossAmount,
            covered.longTermGainLossAmount,
            isBrokerage || isRASOrAPP
          )
        );
      } else {
        // using this function for when there is no covered data to generate only dashed cells
        childRow.push(
          generateCategoryAndAccountTypeRows(
            'no-covered-row',
            'Covered',
            unrealizedTableHeaders(hasBrokerageRASOrAdvisoryFund),
            true,
            isBrokerage ? row.taxesUniqueId : index
          )
        );
      }
      if (uncovered) {
        childRow.push(
          getUnrealizedCoveredRows(
            isBrokerage ? row.taxesUniqueId : index,
            uncovered,
            isBrokerage && !short,
            uncovered.missingCostBasis,
            hasBrokerageRASOrAdvisoryFund,
            uncovered.shortTermGainLossAmount,
            uncovered.longTermGainLossAmount,
            isBrokerage || isRASOrAPP
          )
        );
      }
      if (short) {
        childRow.push(
          getUnrealizedCoveredRows(
            isBrokerage ? row.taxesUniqueId : index,
            short,
            isBrokerage,
            short.missingCostBasis,
            hasBrokerageRASOrAdvisoryFund,
            short.shortTermGainLossAmount,
            short.longTermGainLossAmount,
            isBrokerage || isRASOrAPP
          )
        );
      }
    }

    return {
      className: className,
      rowArray: rowArray,
      accountName: isHolding ? row.accountName : agreement.agreementName,
      agreementID: isBrokerage ? index : parentID,
      productType: agreement.agreementKey.productType.enumerationID,
      childRows: childRow
    };
  }
};

// creates the account level row and adds hidden covered/uncovered rows
export const getUnrealizedRows = (
  index,
  row,
  openedRows,
  setOpenedRows,
  isMFAccount,
  agreement,
  isRetirementAdvisoryServiceClient,
  hasBrokerageRASOrAdvisoryFund,
  hasURZOrRZFail = false,
  isCWV,
  uberId
) => {
  const rowsData = [];
  const className = openedRows[index] ? expandedHeader : toggleIcon;
  // check if pershing to not get uncovered field
  const accountMissingCostBasisData = isMFAccount
    ? checkIfMissingCostBasisData(row.uncovered)
    : row.missingCostBasis;
  const noUncoveredData = isMFAccount
    ? areAllFieldsZero(row.uncovered, unrealizedTableColumns)
    : false;
  const isBrokerage =
    agreement.agreementKey.productType.enumerationID === BROKERAGE;
  if (!isBrokerage) {
    const accountRow = getUnrealizedAccountAndHoldingRow(
      index,
      isMFAccount,
      openedRows,
      setOpenedRows,
      className,
      row,
      accountMissingCostBasisData,
      agreement,
      isRetirementAdvisoryServiceClient,
      hasBrokerageRASOrAdvisoryFund,
      isBrokerage,
      index,
      noUncoveredData,
      true,
      false,
      !isMFAccount ? row.shortTermGainLossAmount : '-',
      !isMFAccount ? row.longTermGainLossAmount : '-',
      hasURZOrRZFail,
      isCWV,
      uberId
    );
    rowsData.push(accountRow);
  } else {
    if (row.recordStatus === 'UNKNOWN' || hasURZOrRZFail) {
      const { agreementKey  } = agreement;
      const productName = getProductName(agreementKey?.productType?.enumerationID);
      const classNm = 'category-row-error';
      let rowArray = [
        {
          record: <CostBasisErrorV2 name={productName} />,
          variant: 'text',
          className: classNm,
          colSpan: '8'
        }
      ];

      rowsData.push({
        className: classNm,
        rowArray: rowArray
      });
    } else {
      row.holdings.map((holding) => {
        const {
          taxesUniqueId,
          missingCostBasis,
          uncovered,
          short,
          shortTermGainLossAmount,
          longTermGainLossAmount
        } = holding;
        const noUncoveredHoldingData = uncovered
          ? areAllFieldsZero(uncovered, unrealizedTableColumns)
          : true;
        const noShortHoldingData = short
          ? areAllFieldsZero(short, unrealizedTableColumns)
          : true;
        const brkHoldingClassName = openedRows[taxesUniqueId]
          ? expandedHeader
          : toggleIcon;

        const holdingRow = getUnrealizedAccountAndHoldingRow(
          taxesUniqueId,
          isMFAccount,
          openedRows,
          setOpenedRows,
          isBrokerage ? brkHoldingClassName : 'holding-row',
          holding,
          missingCostBasis,
          agreement,
          isRetirementAdvisoryServiceClient,
          hasBrokerageRASOrAdvisoryFund,
          isBrokerage,
          index,
          noUncoveredHoldingData,
          noShortHoldingData,
          true,
          shortTermGainLossAmount,
          longTermGainLossAmount,
          false,
          isCWV,
          uberId
        );
        rowsData.push(holdingRow);
      });
    }
  }
  return rowsData;
};

export const getRealizedAccountAndHoldingRow = (
  index,
  row,
  openedRows,
  setOpenedRows,
  className,
  missingCostBasisData,
  agreement,
  noUncoveredData,
  noShortData,
  isBrokerage,
  isHolding = false,
  isPershingAccount = false,
  hasURZOrRZFail = false,
  isCWV
) => {
  const {
    costBasisAmount,
    longTermGainLossAmount,
    proceeds,
    shortTermGainLossAmount,
    totalGainLossAmount,
    totalGainLossPercent,
    recordStatus
  } = row;
  const { agreementKey } = agreement;
  const productName = getProductName(agreementKey?.productType?.enumerationID);
  const productType = agreementKey?.productType?.enumerationID;
  const isRASOrAPP = productType === ACTIVE_PLUS || productType === ACTIVE_ADVISORY;
  const classNm = 'category-row-error';
  let rowArray = [];
  if ((recordStatus === 'UNKNOWN' || hasURZOrRZFail) && isPershingAccount) {
    rowArray = [
      {
        record: <CostBasisErrorV2 name={productName} />,
        variant: 'text',
        className: classNm,
        colSpan: '6'
      }
    ];
    return {
      className: classNm,
      rowArray: rowArray
    };
  } else {
    let childRow = [];
    const {
      covered,
      uncovered,
      short
    } = row;
    const noAccountCoveredData = covered
      ? areAllFieldsZero(covered, realizedTableColumns)
      : true;
    const noAccountUncoveredData = uncovered
      ? areAllFieldsZero(uncovered, realizedTableColumns)
      : true;
    const noAccountShortData = short
      ? areAllFieldsZero(short, realizedTableColumns)
      : true;
    if ((openedRows?.[index] && row.holdings)) {
      row.holdings.map((holding) => {
        const {taxesUniqueId, missingCostBasis, uncovered, short} = holding;
        const noHoldingUncoveredData = uncovered
          ? areAllFieldsZero(uncovered, realizedTableColumns)
          : true;
        const noHoldingShortData = short
          ? areAllFieldsZero(short, realizedTableColumns)
          : true;
        const brkHoldingClassName = openedRows[taxesUniqueId]
          ? expandedHeader
          : toggleIcon;

        const holdingRow = getRealizedAccountAndHoldingRow(
          taxesUniqueId,
          holding,
          openedRows,
          setOpenedRows,
          isBrokerage ? brkHoldingClassName : 'holding-row',
          missingCostBasis,
          agreement,
          noHoldingUncoveredData,
          noHoldingShortData,
          isBrokerage,
          true,
          true,
          false
        );
        childRow.push(holdingRow);
      });
    }
    if (openedRows?.[row.taxesUniqueId] && (!noAccountUncoveredData || !noAccountShortData)) {
      if (covered) {
        childRow.push(
          getRealizedCoveredRows(
            isBrokerage ? row.taxesUniqueId : index,
            covered,
            false,
            noAccountCoveredData,
            covered.missingCostBasis,
            isBrokerage || isRASOrAPP
          )
        );
      } else {
        childRow.push(
          generateCategoryAndAccountTypeRows(
            'no-covered-row',
            'Covered',
            realizedTableColumns,
            true,
            isBrokerage ? row.taxesUniqueId : index
          )
        );
      }
      if (uncovered) {
        childRow.push(
          getRealizedCoveredRows(
            isBrokerage ? row.taxesUniqueId : index,
            uncovered,
            isBrokerage && !short,
            false,
            uncovered.missingCostBasis,
            isBrokerage || isRASOrAPP
          )
        );
      }
      if (short) {
        childRow.push(
          getRealizedCoveredRows(
            isBrokerage ? row.taxesUniqueId : index,
            short,
            isBrokerage,
            false,
            short.missingCostBasis,
            isBrokerage || isRASOrAPP
          )
        );
      }
    }

    return {
      agreementID: index,
      accountName: isHolding ? row.accountName : agreement.agreementName,
      className: className,
      childRows: childRow,
      rowArray: [
        {
          record: (
            <div className="flex-container-v2">
              <div
                className={
                  (!noAccountUncoveredData || !noAccountShortData || (isRASOrAPP && !isHolding))
                    ? 'account-info-container-with-toggle'
                    : isHolding && !isBrokerage
                      ? 'holding-info-container'
                      : accountInfoContainer
                }>
                {(!noAccountUncoveredData || !noAccountShortData || (isRASOrAPP && !isHolding)) && (
                  <ExpandToggle
                    index={index}
                    openedRows={openedRows}
                    setOpenedRows={setOpenedRows}
                    className={className}
                  />
                )}
                <AccountInfo
                  rowData={row}
                  agreement={agreement}
                  isRealized={true}
                  isHolding={isHolding}
                  isRASOrAPP={isRASOrAPP}
                  isCWV={isCWV}
                />
              </div>
            </div>
          ),
          variant: 'text',
          className: className
        },
        {
          record: (
            <div className={'cell-v2'}>
              {missingCostBasisData ? (
                '-'
              ) : (
                <>
                  <div className="value-indicator-icon">
                    <ValueIndicator value={shortTermGainLossAmount} />
                  </div>
                  <Dollar>{shortTermGainLossAmount}</Dollar>
                </>
              )}
            </div>
          ),
          variant: 'text',
          className: className
        },
        {
          record: (
            <div className={'cell-v2'}>
              {missingCostBasisData ? (
                '-'
              ) : (
                <>
                  <div className="value-indicator-icon">
                    <ValueIndicator value={longTermGainLossAmount} />
                  </div>
                  <Dollar>{longTermGainLossAmount}</Dollar>
                </>
              )}
            </div>
          ),
          variant: 'text',
          className: className
        },
        {
          record: (
            <div className={'cell-v2'}>
              {missingCostBasisData ? (
                '-'
              ) : (
                <>
                  <div className="value-indicator-icon">
                    <ValueIndicator value={totalGainLossAmount} />
                  </div>
                  <Dollar>{totalGainLossAmount}</Dollar>
                  <br />
                  <div className={percentGainLossClass}>
                    {numeral(isBrokerage || isRASOrAPP ? totalGainLossPercent : (totalGainLossPercent * 100)).format('0,0.00')}%
                  </div>
                </>
              )}
            </div>
          ),
          variant: 'text',
          className: className
        },
        {
          record: (
            <div className={'cell-v2'}>
              <Dollar>{proceeds}</Dollar>
            </div>
          ),
          variant: 'text',
          className: className
        },
        {
          record: (
            <div className={'cell-v2'}>
              {missingCostBasisData ? (
                '-'
              ) : (
                <Dollar>{costBasisAmount}</Dollar>
              )}
            </div>
          ),
          variant: 'text',
          className: className
        }
      ]
    };
  }
};

export const getRealizedRows = (
  index,
  row,
  openedRows,
  setOpenedRows,
  agreement,
  isPershingAccount,
  hasURZOrRZFail = false
) => {
  const rowsData = [];
  const className = openedRows[index] ? 'expanded-header' : 'toggle-icon';
  const missingCostBasisData = isPershingAccount
    ? row.missingCostBasis
    : checkIfMissingCostBasisData(row.uncovered);
  /*const noCoveredData = !isPershingAccount
    ? areAllFieldsZero(row.covered, realizedTableColumns)
    : false;*/
  const noUncoveredData = !isPershingAccount
    ? areAllFieldsZero(row.uncovered, realizedTableColumns)
    : false;
  const isBrokerage =
    agreement.agreementKey.productType.enumerationID === BROKERAGE;

  if (!isBrokerage) {
    const accountRow = getRealizedAccountAndHoldingRow(
      index,
      row,
      openedRows,
      setOpenedRows,
      className,
      missingCostBasisData,
      agreement,
      noUncoveredData,
      true,
      false,
      false,
      isPershingAccount,
      hasURZOrRZFail
    );
    rowsData.push(accountRow);
  }
  if (isBrokerage) {
    if (row.recordStatus === 'UNKNOWN' || hasURZOrRZFail) {
      const { agreementKey} = agreement;
      const productName = getProductName(agreementKey?.productType?.enumerationID);
      const classNm = 'category-row-error';
      let rowArray = [
        {
          record: <CostBasisErrorV2 name={productName} />,
          variant: 'text',
          className: classNm,
          colSpan: '6'
        }
      ];

      rowsData.push({
        className: classNm,
        rowArray: rowArray
      });
    } else {
      row.holdings.map((holding) => {
        const { taxesUniqueId, missingCostBasis, uncovered, short } = holding;
        const noHoldingUncoveredData = !uncovered;
        const noHoldingShortData = short
          ? areAllFieldsZero(short, realizedTableColumns)
          : true;
        const brkHoldingClassName = openedRows[taxesUniqueId]
          ? expandedHeader
          : toggleIcon;

        const holdingRow = getRealizedAccountAndHoldingRow(
          taxesUniqueId,
          holding,
          openedRows,
          setOpenedRows,
          isBrokerage ? brkHoldingClassName : 'holding-row',
          missingCostBasis,
          agreement,
          noHoldingUncoveredData,
          noHoldingShortData,
          isBrokerage,
          true
        );
        rowsData.push(holdingRow);
      });
    }
  }
  return rowsData;
};
